import type { FC, PropsWithChildren } from 'react';
import { useEffect } from 'react';
import { useLocation } from 'react-router';
import { usePollPlatformStatus } from '../../api/platformStatus/platformStatus';
import { usePollVersionInfo } from '../../api/version/version';
import { PATHNAMES } from '../../constants/constants';
import { BrowserToast } from '../BrowserToast/BrowserToast';
import { Header } from '../Header';
import { IssuesBanner } from '../IssuesBanner/IssuesBanner';
import { UpdateVersionAlert } from '../UpdateVersionAlert/UpdateVersionAlert';
import s from './Layout.module.scss';

const AUTH0_CACHE_PREFIX = '@@auth0spajs@@';
const RELOADED_FOR_VERSION_KEY = 'reloadedForVersion';

const isChrome = (): boolean => {
  const userAgent = navigator.userAgent;
  return userAgent.includes('Chrome');
};

export const Layout: FC<PropsWithChildren> = ({ children }) => {
  'use memo';
  const { pathname } = useLocation();
  const isPatentIntelligence = pathname === PATHNAMES.patentIntelligence;

  const { data: platformStatus } = usePollPlatformStatus();
  const { data: versionInfo, refetch: refetchVersionInfo } = usePollVersionInfo();
  const currentVersion = import.meta.env.VITE_APP_VERSION ?? '';

  const hasNewerVersion = versionInfo && versionInfo.version !== currentVersion;

  // Another tab writing new login tokens means the user is working there, so a stale background tab can reload.
  useEffect(() => {
    const reloadIfOutdated = (event: StorageEvent) => {
      if (!document.hidden || !event.key?.startsWith(AUTH0_CACHE_PREFIX)) return;
      void refetchVersionInfo().then(({ data }) => {
        if (!document.hidden || !data || data.version === currentVersion) return;
        // Once per version, so tabs that reload onto a stale copy can't keep waking each other.
        if (sessionStorage.getItem(RELOADED_FOR_VERSION_KEY) === data.version) return;
        sessionStorage.setItem(RELOADED_FOR_VERSION_KEY, data.version);
        window.location.reload();
      });
    };
    window.addEventListener('storage', reloadIfOutdated);
    return () => window.removeEventListener('storage', reloadIfOutdated);
  }, [refetchVersionInfo, currentVersion]);

  return (
    <div className={s.pageWrapper}>
      {!isPatentIntelligence && <Header />}
      {!isChrome() && <BrowserToast />}
      {hasNewerVersion && <UpdateVersionAlert />}
      {platformStatus && !platformStatus.ok && <IssuesBanner message={platformStatus?.data?.message ?? null} />}
      {children}
    </div>
  );
};
